import { apiClient, apiClientPaginated, apiBlob, ApiError } from '@/lib/api-client';
import type {
  CreateVendorBillPayload,
  PurchaseDocument,
  PurchaseDocumentPayload,
  RecordVendorPaymentPayload,
  UpdateVendorBillPayload,
  VendorBill,
  VendorBillListParams,
  VendorBillListResponse,
  VendorBillStats,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) return fallback;
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== false);
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_STATS: VendorBillStats = {
  open_count: 0, partially_paid_count: 0, paid_count: 0, overdue_count: 0, outstanding_total: 0, overdue_total: 0, paid_this_month: 0,
};

export const vendorBillsApi = {
  // Not wrapped in withFallback — a dead endpoint must surface as an error, not "no bills".
  list: (params: VendorBillListParams) =>
    apiClientPaginated<VendorBillListResponse['data']>(
      `/vendor-bills${buildQuery({
        page: params.page, limit: params.limit, status: params.status || undefined, supplier_id: params.supplier_id,
        purchase_order_id: params.purchase_order_id, search: params.search, start_date: params.start_date,
        end_date: params.end_date, overdue: params.overdue,
      })}`,
    ) as Promise<VendorBillListResponse>,

  stats: () => withFallback(() => apiClient<VendorBillStats>('/vendor-bills/stats'), EMPTY_STATS),

  // Not wrapped in withFallback — a 404 must surface so the detail page leaves its skeleton.
  getById: (id: string) => apiClient<VendorBill>(`/vendor-bills/${id}`),

  create: (payload: CreateVendorBillPayload) => apiClient<VendorBill>('/vendor-bills', { method: 'POST', body: payload }),

  update: (id: string, payload: UpdateVendorBillPayload) => apiClient<VendorBill>(`/vendor-bills/${id}`, { method: 'PUT', body: payload }),

  /** DRAFT → OPEN. A vendor invoice number already on another bill of the supplier needs confirmDuplicate. */
  post: (id: string, confirmDuplicate = false) =>
    apiClient<VendorBill>(`/vendor-bills/${id}/post`, { method: 'POST', body: confirmDuplicate ? { confirm_duplicate: true } : undefined }),

  recordPayment: (id: string, payload: RecordVendorPaymentPayload) =>
    apiClient<VendorBill>(`/vendor-bills/${id}/payments`, { method: 'POST', body: payload }),

  /** OWNER only (QA PU4-005): the payment stays on the bill, marked void. */
  voidPayment: (id: string, paymentId: string, reason: string) =>
    apiClient<VendorBill>(`/vendor-bills/${id}/payments/${paymentId}/void`, { method: 'POST', body: { reason } }),

  addDocument: (id: string, payload: PurchaseDocumentPayload) =>
    apiClient<VendorBill>(`/vendor-bills/${id}/documents`, { method: 'POST', body: payload }),

  cancel: (id: string, reason: string) => apiClient<VendorBill>(`/vendor-bills/${id}/cancel`, { method: 'POST', body: { reason } }),

  /** A4 PDF of the bill; not wrapped in withFallback — a 404 must surface. */
  downloadPdf: (id: string) => apiBlob(`/vendor-bills/${id}/pdf`),

  // PO-level documents (vendor invoices / challans not tied to a bill)
  addPoDocument: (poId: string, payload: PurchaseDocumentPayload) =>
    apiClient<PurchaseDocument>(`/purchase-orders/${poId}/documents`, { method: 'POST', body: payload }),

  deletePoDocument: (poId: string, docId: string) =>
    apiClient<{ deleted: boolean }>(`/purchase-orders/${poId}/documents/${docId}`, { method: 'DELETE' }),
};
